import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import type { DrawerItem } from '@/utils/routes'
import { cn } from '@/lib/utils'

/** Focus ring that stays visible on the dark sidebar surface. */
const SIDEBAR_FOCUS =
  'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--color-sidebar-bg),0_0_0_4px_var(--color-sidebar-text)]'

export const isRouteActive = (pathname: string, route: string) =>
  pathname === route || (route !== '/leaves' && pathname.startsWith(route + '/'))

function NavIcon({ item }: { item: DrawerItem }) {
  // Route icons are 18px lucide elements; normalise to the 20px nav size.
  return typeof item.icon !== 'string' && item.icon ? (
    <span className="inline-flex shrink-0 [&_svg]:size-5" aria-hidden="true">
      {item.icon}
    </span>
  ) : (
    <span className="inline-flex size-5 shrink-0 items-center justify-center font-semibold" aria-hidden="true">
      {item.text.slice(0, 2).toUpperCase()}
    </span>
  )
}

// ── nav item ──────────────────────────────────────────────────────────────────
interface NavItemProps {
  item: DrawerItem
  depth?: number
  collapsed?: boolean
}

function navClass(active: boolean, collapsed: boolean, depth: number) {
  return cn(
    'group flex h-10 w-full items-center gap-3 rounded-md text-base font-medium text-sidebar-text transition-colors duration-fast hover:bg-sidebar-hover',
    SIDEBAR_FOCUS,
    collapsed ? 'justify-center px-0' : 'px-4',
    depth > 0 && !collapsed && 'pl-10',
    active && 'bg-sidebar-hover font-semibold shadow-[inset_3px_0_0_var(--color-primary)]',
  )
}

function NavItem({ item, depth = 0, collapsed = false }: NavItemProps) {
  const location = useLocation()
  const hasChildren = item.children && item.children.length > 0
  const isActive = isRouteActive(location.pathname, item.route)

  if (hasChildren) {
    const anyChildActive = item.children.some((c) => isRouteActive(location.pathname, c.route))
    return <NavGroup item={item} depth={depth} collapsed={collapsed} defaultExpanded={anyChildActive} />
  }

  return (
    <li>
      <Link
        to={item.route}
        title={collapsed ? item.text : undefined}
        aria-current={isActive ? 'page' : undefined}
        aria-label={collapsed ? item.text : undefined}
        className={navClass(isActive, collapsed, depth)}
      >
        <NavIcon item={item} />
        {collapsed ? null : <span className="truncate">{item.text}</span>}
      </Link>
    </li>
  )
}

function NavGroup({
  item,
  depth,
  collapsed,
  defaultExpanded,
}: {
  item: DrawerItem
  depth: number
  collapsed: boolean
  defaultExpanded: boolean
}) {
  const location = useLocation()
  const isActive = isRouteActive(location.pathname, item.route)
  const [expanded, setExpanded] = React.useState(defaultExpanded)
  const listId = `nav-group-${item.id}`

  return (
    <li>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={listId}
        aria-label={collapsed ? item.text : undefined}
        title={collapsed ? item.text : undefined}
        className={cn(navClass(isActive, collapsed, depth), !collapsed && 'justify-between')}
      >
        <span className="flex min-w-0 items-center gap-3">
          <NavIcon item={item} />
          {collapsed ? null : <span className="truncate">{item.text}</span>}
        </span>
        {collapsed ? null : expanded ? (
          <ChevronDown className="size-4 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="size-4 shrink-0" aria-hidden="true" />
        )}
      </button>
      {expanded && (
        <ul id={listId} className="mt-0.5 flex flex-col gap-0.5">
          {item.children.map((child) => (
            <NavItem key={child.id} item={child} depth={depth + 1} collapsed={collapsed} />
          ))}
        </ul>
      )}
    </li>
  )
}

// ── sidebar shell ─────────────────────────────────────────────────────────────
export interface SidebarProps {
  title: string
  items: DrawerItem[]
  collapsed: boolean
  onToggle: () => void
  navRef?: React.RefObject<HTMLElement>
}

/** Brand mark: flat square, no glow. */
export function BrandMark() {
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary font-semibold text-primary-on" aria-hidden="true">
      OP
    </div>
  )
}

export function Sidebar({ title, items, collapsed, onToggle, navRef }: SidebarProps) {
  return (
    <aside
      data-collapsed={collapsed}
      className={cn(
        'sticky top-0 z-sidebar hidden h-screen shrink-0 flex-col bg-sidebar text-sidebar-text transition-[width] duration-base md:flex',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <nav ref={navRef} aria-label="Primary" className="flex min-h-0 flex-1 flex-col">
        <div className={cn('flex h-topbar shrink-0 items-center border-b border-sidebar-hover', collapsed ? 'justify-center' : 'gap-3 px-4')}>
          <BrandMark />
          {collapsed ? null : (
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold">Fulfillment Panel</span>
              <span className="truncate text-sidebar-muted">{title}</span>
            </div>
          )}
        </div>

        {collapsed ? null : <p className="px-4 pb-1 pt-4 font-medium text-sidebar-muted">Workspace</p>}

        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          <ul className="flex flex-col gap-0.5">
            {items.map((item) => (
              <NavItem key={item.id} item={item} collapsed={collapsed} />
            ))}
          </ul>
        </div>
      </nav>

      <div className={cn('flex shrink-0 items-center border-t border-sidebar-hover p-2', collapsed ? 'justify-center' : 'justify-between pl-4')}>
        {collapsed ? null : <span className="text-sidebar-muted">Panel v1.0</span>}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand navigation (Shift+B)' : 'Collapse navigation (Shift+B)'}
          className={cn(
            'inline-flex size-9 items-center justify-center rounded-md text-sidebar-text hover:bg-sidebar-hover',
            SIDEBAR_FOCUS,
          )}
        >
          {collapsed ? <PanelLeftOpen className="size-4" aria-hidden="true" /> : <PanelLeftClose className="size-4" aria-hidden="true" />}
        </button>
      </div>
    </aside>
  )
}
